import { Injectable } from '@angular/core';
import { TaskProgressStatus } from '@app/shared/interfaces';

export interface ProgressStep {
  label: string;
  status: TaskProgressStatus;
  isComplete: boolean;
  isActive: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class TaskProgressService {
  private readonly progressSteps: ReadonlyArray<{ label: string; status: TaskProgressStatus }> = [
    { label: 'Not started', status: 'not_started' },
    { label: 'On the way', status: 'on_the_way' },
    { label: 'Arrived', status: 'arrived' },
    { label: 'In progress', status: 'in_progress' },
    { label: 'Completed', status: 'completed' },
  ];

  /**
   * Get all progress steps with their completion status based on the current progress
   * @param currentStatus The current progress status from the API
   * @returns Array of progress steps with their completion states
   */
  getProgressSteps(currentStatus: TaskProgressStatus | string): ProgressStep[] {
    const currentIndex = this.getStatusIndex(currentStatus as TaskProgressStatus);

    return this.progressSteps.map((step, index) => ({
      label: step.label,
      status: step.status,
      isComplete: index < currentIndex || (step.status === 'completed' && currentStatus === 'completed'),
      isActive: index === currentIndex,
    }));
  }

  /**
   * Get the index of a status in the progress flow
   * @param status The progress status
   * @returns The index of the status, or -1 if not found
   */
  private getStatusIndex(status: TaskProgressStatus): number {
    return this.progressSteps.findIndex((step) => step.status === status);
  }

  /**
   * Check if a specific step is complete based on the current status
   * @param stepStatus The status to check
   * @param currentStatus The current progress status
   * @returns true if the step is complete
   */
  isStepComplete(stepStatus: TaskProgressStatus, currentStatus: TaskProgressStatus | string): boolean {
    const stepIndex = this.getStatusIndex(stepStatus);
    const currentIndex = this.getStatusIndex(currentStatus as TaskProgressStatus);

    return stepIndex < currentIndex || (stepStatus === 'completed' && currentStatus === 'completed');
  }

  /**
   * Check if a specific step is active (current step)
   * @param stepStatus The status to check
   * @param currentStatus The current progress status
   * @returns true if the step is active
   */
  isStepActive(stepStatus: TaskProgressStatus, currentStatus: TaskProgressStatus | string): boolean {
    return stepStatus === currentStatus;
  }

  /**
   * Get the percentage of completion based on the current status
   * @param currentStatus The current progress status
   * @returns Percentage of completion (0-100)
   */
  getProgressPercentage(currentStatus: TaskProgressStatus | string): number {
    const currentIndex = this.getStatusIndex(currentStatus as TaskProgressStatus);

    if (currentIndex === -1) return 0;

    // If we're at 'done', return 100%
    if (currentIndex === this.progressSteps.length - 1) {
      return 100;
    }

    // Otherwise, calculate percentage based on completed steps
    return Math.round((currentIndex / (this.progressSteps.length - 1)) * 100);
  }

  /**
   * Get a human-readable label for a status
   * @param status The progress status
   * @returns The human-readable label
   */
  getStatusLabel(status: TaskProgressStatus | string): string {
    const step = this.progressSteps.find((s) => s.status === status);
    return step?.label || status;
  }

  /**
   * Get the next status in the progress flow
   * @param currentStatus The current progress status
   * @returns The next status, or null if at the end
   */
  getNextStatus(currentStatus: TaskProgressStatus | string): TaskProgressStatus | null {
    const currentIndex = this.getStatusIndex(currentStatus as TaskProgressStatus);

    if (currentIndex === -1 || currentIndex >= this.progressSteps.length - 1) {
      return null;
    }

    return this.progressSteps[currentIndex + 1].status;
  }

  /**
   * Get the previous status in the progress flow
   * @param currentStatus The current progress status
   * @returns The previous status, or null if at the beginning
   */
  getPreviousStatus(currentStatus: TaskProgressStatus | string): TaskProgressStatus | null {
    const currentIndex = this.getStatusIndex(currentStatus as TaskProgressStatus);

    if (currentIndex <= 0) {
      return null;
    }

    return this.progressSteps[currentIndex - 1].status;
  }
}
